import { Divider, Form, Spin } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "../styles.css";
import InviteContent from "../components/invite-content";
import EnrollmentForm from "../enrollment-form/enrollment-form";
import ModalWrapper from "../../../modal/modal";
import useNotification from "../../../notification/notification";
import ApiSubjectsManagement from "../../../../api/subjects-manager";
import ApiEnrollmentScenarioManagement from "../../../../api/enrollment-scenario-management";
import { LoadingOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { selectIsSystemAdmin } from "../../../../store/slices/user";
import { selectSettings } from "../../../../store/slices/settings";

const InviteSubjectModal = ({
  isOpen,
  close,
  fetchEnrollInviteUserTemplate,
  sites
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const [inProgress, setProgress] = useState(false);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [graph, setGraph] = useState(null);
  const [fields, setFields] = useState([]);
  const [extraFields, setExtraFields] = useState([]);
  const [enrollmentState, setEnrollmentState] = useState({ complete: false, email: "", customFields: {} });
  const [artificial, setArtificial] = useState(false);
  const isSystemAdmin = useSelector(selectIsSystemAdmin);
  const settings = useSelector(selectSettings);
  const sitesEnabled = settings?.find((s) => s.key === "sitesEnable")?.value === true;
  const [siteId, setSiteId] = useState(null);

  // siteId / artificial are not graph nodes — handled via the small extras form.
  const onExtraSelectChange = (id, value) => {
    if (id === "artificial") {
      setArtificial(value);
    } else if (id === "siteId") {
      setSiteId(value);
    }
  };

  const onClose = () => {
    close();
    form.resetFields();
    setArtificial(false);
    setSiteId(null);
    setEnrollmentState({ complete: false, email: "", customFields: {} });
  };

  const inviteSubject = () => {
    if (!enrollmentState.complete) {
      openNotification({
        type: "error",
        message: <p>{t("invite_subject.notification.error")}</p>,
      });
      return;
    }

    setProgress(true);
    const newSubject = {
      email: enrollmentState.email,
      artificial,
      siteId,
      customFields: enrollmentState.customFields,
    };

    ApiSubjectsManagement.invite(newSubject)
      .then((res) => {
        const code = res?.data?.code;
        const isExisting = code === "EXISTING_ACCOUNT_ADDED";
        openNotification({
          type: "success",
          message: (
            <p>
              {isExisting
                ? "The subject was added to the project without an invitation, as they already have a LifeFit360 account."
                : t("invite_subject.notification.success", { email: newSubject.email })}
            </p>
          ),
        });
      })
      .catch((e) => {
        openNotification({
          type: "error",
          message: (
            <p>
              {t(`invite_subject.notification.${e?.response?.data?.error?.code || "error"}`)}
            </p>
          ),
        });
      })
      .finally(() => {
        setProgress(false);
        onClose();
      });
  };

  useEffect(() => {
    if (!isOpen) {return;}
    setIsLoadingContent(true);

    Promise.all([
      ApiEnrollmentScenarioManagement.getEnrollmentScenarioForm(),
      fetchEnrollInviteUserTemplate ? fetchEnrollInviteUserTemplate() : Promise.resolve({ data: { enrollmentFields: [] } }),
    ])
      .then(([scenarioRes, fieldsRes]) => {
        setGraph(scenarioRes?.data?.enrollmentScenario?.data || {});
        setFields(fieldsRes?.data?.enrollmentFields || []);

        const extras = [];
        // LF360-2024: whoever invites; the server holds a caller limited to
        // their sites to one of them.
        if (sitesEnabled) {
          extras.push({
            id: "siteId",
            label: t("invite_subject.fields.site"),
            type: "select",
            options: sites,
            placeholder: "Select site",
            default: false,
          });
        }
        if (isSystemAdmin) {
          extras.push({
            id: "artificial",
            label: t("invite_subject.fields.artificial"),
            type: "boolean",
            default: false,
          });
        }
        setExtraFields(extras);
      })
      .catch(() => {
        openNotification({ type: "error", message: <p>Failed to load the invitation form.</p> });
      })
      .finally(() => setIsLoadingContent(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, fetchEnrollInviteUserTemplate]);

  return (
    <ModalWrapper
      isOpen={isOpen}
      title={t("invite_subject.title")}
      onOk={() => inviteSubject()}
      onCancel={onClose}
      okText={t("invite_subject.invite")}
      okButtonProps={{
        size: "large",
        disabled: !enrollmentState.complete || isLoadingContent,
        "data-testid": "invite-subject-submit",
      }}
      cancelButtonProps={{
        size: "large",
        style: { backgroundColor: "rgba(108, 108, 137, 0.08)" },
      }}
      confirmLoading={inProgress}
    >
      <Divider />
      {isLoadingContent ? (
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
      ) : (
        <div className="invite-subject__form" data-testid="invite-subject-form">
          {/* The enrollment questions follow the same row as every other form on
              the site — label on the left, control on the right, no border —
              which is also what the extras below them use (LF360-1844). */}
          <EnrollmentForm
            graph={graph}
            fields={fields}
            onChange={setEnrollmentState}
            borderless
            fieldClassName="invite-user__field"
          />
          {extraFields.length > 0 && (
            <Form form={form}>
              <InviteContent fields={extraFields} onSelectChange={onExtraSelectChange} />
            </Form>
          )}
        </div>
      )}
      <Divider />
    </ModalWrapper>
  );
};

export default InviteSubjectModal;
